//NOTE: This widget's definition is included in powerSchoolDesignSystemToolkit.ts
import {InputSelectorResult, PdsAbstractInputWidget} from './pds-abstract-input-widget';
import { BasicValidationRules } from 'common-utils/interfaces';
import { pdsValidationRulesEngine } from './pds-validation-rules-engine';
import {StringBoolean} from './pds-widget';

export class PdsTextareaWidget extends PdsAbstractInputWidget {
  is = 'pds-textarea-widget';

  //Text Labels
  dataRegexErrorText: string;
  dataInverseRegexErrorText: string;

  //Validation Rules
  dataRegex: string;
  dataRegexmodifiers: string;
  dataInverseRegex: string;
  dataInverseRegexmodifiers: string;
  dataMinlength: string;
  dataMaxlength: string;
  dataExcludechars: string;
  currentCharCount = 0;
  dataShowCharCounter: StringBoolean;

  //Used to cache the regex so we don't keep re-creating it.
  validationRegex: RegExp;

  properties = {
    //AbstractInputWidget Properties
    name: String, //We construct the id using the name property
    dataLabelText: String,
    dataShowLabel: {
      type: String,
      value: null
    },
    dataBadgeText: {
      type: String,
      value: '',
      observer: 'setUpBadge'
    },
    dataHelperText: {
      type: String,
      value: null
    },
    dataFieldHelpText: String,
    dataPlaceholderText: String,
    dataTooltipText: String,
    dataUseAlternateColorScheme: {
      type: String,
      value: 'false'
    },
    dataIsreadonly: {
      type: String,
      value: 'false',
      observer: 'setReadonlyText'
    },
    dataIsfieldonly: {
      //This is for adding a class to get rid of the bottom padding.
      type: String,
      value: 'false'
    },
    dataHideUnelevatedBadge: {
      type: String,
      value: 'false',
      observer: 'setUpBadge'
    },
    dataDisableInvalidBadgeElevation: {
      type: String,
      value: 'false',
      observer: 'setUpBadge'
    },
    //Widget specific Validation Properties
    dataRegexErrorText: String,
    dataInverseRegexErrorText: String,
    dataIsrequired: {
      type: String,
      value: 'false',
      observer: 'setUpBadge'
    },
    disabled: {
      type: String,
      value: 'false'
    },
    //Option to activate the validation indicator
    //The validation indicator example shows up in the central-layout-preview page on the PDS-Site
    dataUseValidIndicator: {
      type: String,
      value: 'false'
    },
    dataMinlength: String,
    dataMaxlength: {
      type: String,
      value: null,
      observer: 'setDefaultShowCharCounter'
    },
    dataExcludechars: String,
    dataRegex: String,
    dataRegexmodifiers: String,
    dataInverseRegex: String,
    dataInverseRegexmodifiers: String,
    dataCustomvalidate: String,
    dataActionIcon: String,
    dataShowCharCounter: {
      type: String,
      value: null
    }
  };

  ready(): void {
    super.ready();
    this.currentCharCount = this.value.length;
  }

  setDefaultShowCharCounter(newValue, oldValue): void {
    if (newValue == null && oldValue == null) {
      //It didn't really change. Get out of here.
      return;
    }

    if (this.dataShowCharCounter == null) {
      let maxLength = Number(this.dataMaxlength);
      //We default this to off when there is no max length, and on when there is a max length.
      if (!isNaN(maxLength) && maxLength > 0) {
        this.dataShowCharCounter = 'true';
      }
    }
  }

  generateValidationRules(): void {
    let validationRules: BasicValidationRules = pdsValidationRulesEngine.getBasicValidationRules();
    validationRules.isText = true;
    validationRules.setProperty('isRequired', this.dataIsrequired, Boolean);
    validationRules.setProperty('maxLength', this.dataMaxlength, Number);
    validationRules.setProperty('minLength', this.dataMinlength, Number);
    validationRules.setProperty('excludeChars', this.dataExcludechars, String);

    //This is more complicated so we can't set it through setProperty.
    if (
      this.dataRegex != null &&
      this.dataRegex.length > 0 &&
      this.dataRegexErrorText != null &&
      this.dataRegexErrorText.length > 0
    ) {
      try {
        validationRules.regex = {
          pattern: this.dataRegex != null ? new RegExp(this.dataRegex, this.dataRegexmodifiers) : new RegExp(this.dataRegex),
          errorMessage: this.dataRegexErrorText
        };
      } catch (err) {
        //When an invalid number gets put into a number property, the property in validationRules is not set with bad data.
        //And no error is logged. We will do the same thing here.
      }
    }

    if (
      this.dataInverseRegex != null &&
      this.dataInverseRegex.length > 0 &&
      this.dataInverseRegexErrorText != null &&
      this.dataInverseRegexErrorText.length > 0
    ) {
      try {
        validationRules.inverseRegex = {
          pattern: this.dataInverseRegex != null
            ? new RegExp(this.dataInverseRegex, this.dataInverseRegexmodifiers)
            : new RegExp(this.dataInverseRegex),
          errorMessage: this.dataInverseRegexErrorText
        };
      } catch (err) {
        //When an invalid number gets put into a number property, the property in validationRules is not set with bad data.
        //And no error is logged. We will do the same thing here.
      }
    }

    this.validators = pdsValidationRulesEngine.getValidatorsForRules(validationRules);
    if ('dataCustomvalidate' in this) {
      this.validators.push({
        execute: this.pds.validation.getCustomValidate(this.dataCustomvalidate),
        condition: {
          element: this
        }
      });
    }
  }

  /**
   * @param modelValue
   * @param skip
   * @returns returns if the model value was actually set.
   */
  setModelValue(modelValue: string, skip: boolean): boolean {
    let ele = this.inputSelector();
    if (ele != null && modelValue !== ele.modelValue) {
      ele.modelValue = modelValue;
      if (skip !== true) {
        this.setValue(modelValue, true);
      }
      return true;
    }
    return false;
  }

  /**
   *
   * @param value - The string the user typed into the input.
   * @param skip - skips setting the model value. (Keeps from getting into recursive loops)
   */
  setValue(value: string, skip = false): void {
    if (value == null) {
      value = '';
    }

    let inputEl = this.inputSelector();
    if (inputEl != null && value !== inputEl.value) {
      inputEl.value = value;
    }

    if (skip !== true) {
      this.setModelValue(value, true);
    }

    this.currentCharCount = this.value.length;
  }

  inputSelector(): HTMLTextAreaElement & InputSelectorResult {
    return this.querySelector('textarea');
  }

  getCharactersRemaining(currentCharCount?: number, maxLenght?: number): number {
    let difference: number = Number(this.dataMaxlength) - this.currentCharCount;
    if (isNaN(difference) || difference < 0) {
      return 0;
    }
    return difference;
  }

}
